/**
 * MarkdownView — the single shared markdown renderer for the Arbi platform.
 *
 * Day 100. Wraps react-markdown (+ remark-gfm + rehype-slug) with Arbi design-token styling
 * (Inter body / JetBrains Mono code / semantic ink+surface tokens — no
 * hardcoded color, per UI_STANDARDS §4). Dependency-light by design: it takes a
 * markdown string and nothing else (no AppContext, no providers), so it
 * re-exports cleanly into the thin arbi-docs app and into arbi-dev via the
 * ADR-36 sibling re-export shim.
 *
 * Used by the arbi-dev Docs CMS preview (/admin/docs), the arbi-docs Help-docs
 * render, AND (Day 100b.2) the arbi-root in-app Help overlay Docs tab — a single
 * renderer guarantees all three are byte-identical (the RBP:1387 round-trip). The
 * overlay is the FIRST arbi-root consumer, so react-markdown + remark-gfm +
 * rehype-slug now ship in the arbi-root bundle too (no NEW dep — added 100b.1; a
 * bundle-composition change, accepted).
 *
 * Internal-link nav (Day 100b.2 — DOC_STANDARDS §3 Related, BACKLOG L26): the
 * optional onInternalLink prop lets a host client-navigate a bare-slug link
 * instead of opening a new tab. Detection is SYNTACTIC here (no scheme / no
 * leading slash / no #/mailto); slug RESOLUTION (known vs unknown) is the host's,
 * so MarkdownView stays markdown-only + dependency-light. Absent the prop, links
 * keep the default target="_blank" behavior.
 *
 * NO rehype-raw: embedded HTML in markdown is NOT rendered (XSS-safe by
 * construction; raw-HTML support + sanitization stays deferred). rehype-slug
 * adds heading ids (Day 100b.1) — invisible, so the arbi-dev CMS preview +
 * arbi-docs stay render-identical; the anchor-TOC that consumes those ids lives
 * in the arbi-docs HelpDocs chrome (NOT here). The DOC_STANDARDS article
 * template is §3 (authored Day 100b.0); revision chrome is the last-updated
 * date only (item-7 — the docs_page_revisions table is deferred per decision #2).
 */
import { useMemo, type JSX } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSlug from 'rehype-slug';
import clsx from 'clsx';

interface MarkdownViewProps {
  markdown: string;
  className?: string;
  /** Optional host handler for internal bare-slug links (DOC_STANDARDS §3 Related,
   *  e.g. `[Receive a purchase order](receive-a-purchase-order)`). When provided,
   *  the `a` renderer detects an internal bare-slug href SYNTACTICALLY (truthy AND
   *  not http(s):/mailto:/#/leading-slash), preventDefaults, and calls this with
   *  the href as the slug. Resolution (known vs unknown) is the host's job —
   *  MarkdownView never imports the registry. Absent the prop, the default
   *  target="_blank" behavior is unchanged. (Day 100b.2 — BACKLOG L26.) */
  onInternalLink?: (slug: string) => void;
}

// Element → Arbi-token styling. Tokens only (no hardcoded hex); block code
// chrome lives on <pre> so the inner <code> only carries the mono font.
// Headings forward rehype-slug's `id` to the DOM so the arbi-docs anchor-TOC
// (HelpDocs chrome) can target them via in-page #id links.
const COMPONENTS: Components = {
  h1: ({ children, id }) => (
    <h1 id={id} className="text-xl font-semibold text-ink-bright mt-5 mb-2 first:mt-0">
      {children}
    </h1>
  ),
  h2: ({ children, id }) => (
    <h2 id={id} className="text-lg font-semibold text-ink-bright mt-5 mb-2 first:mt-0">
      {children}
    </h2>
  ),
  h3: ({ children, id }) => (
    <h3 id={id} className="text-base font-semibold text-ink-bright mt-4 mb-1.5 first:mt-0">
      {children}
    </h3>
  ),
  h4: ({ children, id }) => (
    <h4 id={id} className="text-sm font-semibold text-ink-bright mt-3 mb-1 first:mt-0">
      {children}
    </h4>
  ),
  p: ({ children }) => <p className="my-2.5">{children}</p>,
  // In-body #anchor links must resolve IN PAGE (native fragment scroll to the
  // rehype-slug heading ids) — target="_blank" on a hash href opens a new tab
  // at the same URL instead of scrolling (T3.9 browser-confirm finding; the
  // 100b.2 overlay check (d) expects anchors to resolve in the overlay render).
  a: ({ href, children }) =>
    href && href.startsWith('#') ? (
      <a href={href} className="text-accent underline-offset-2 hover:underline">
        {children}
      </a>
    ) : (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-accent underline-offset-2 hover:underline"
      >
        {children}
      </a>
    ),
  ul: ({ children }) => <ul className="my-2.5 ml-5 list-disc space-y-1">{children}</ul>,
  ol: ({ children }) => <ol className="my-2.5 ml-5 list-decimal space-y-1">{children}</ol>,
  li: ({ children }) => <li>{children}</li>,
  blockquote: ({ children }) => (
    <blockquote className="my-3 border-l-2 border-border-strong pl-3 text-ink-secondary italic">
      {children}
    </blockquote>
  ),
  hr: () => <hr className="my-4 border-border" />,
  strong: ({ children }) => <strong className="font-semibold text-ink-bright">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  code: ({ className, children }) => {
    const isBlock = /language-/.test(className ?? '');
    return isBlock ? (
      <code className={clsx('font-mono', className)}>{children}</code>
    ) : (
      <code className="font-mono text-[0.85em] bg-surface-2 text-ink-bright px-1 py-0.5 rounded">
        {children}
      </code>
    );
  },
  pre: ({ children }) => (
    <pre className="my-3 overflow-x-auto rounded-lg border border-border bg-surface-2 p-3 text-[0.85em]">
      {children}
    </pre>
  ),
  table: ({ children }) => (
    <div className="my-3 overflow-x-auto">
      <table className="w-full border-collapse text-xs">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="border-b border-border">{children}</thead>,
  th: ({ children }) => (
    <th className="px-2 py-1.5 text-left font-semibold text-ink-secondary">{children}</th>
  ),
  td: ({ children }) => (
    <td className="border-t border-border-subtle px-2 py-1.5 align-top">{children}</td>
  ),
  img: ({ src, alt }) => (
    <img
      src={typeof src === 'string' ? src : undefined}
      alt={alt ?? ''}
      className="my-3 max-w-full rounded-lg border border-border"
    />
  ),
};

export default function MarkdownView({
  markdown,
  className,
  onInternalLink,
}: MarkdownViewProps): JSX.Element {
  // Absent onInternalLink, use the static COMPONENTS verbatim (default
  // target="_blank" links — unchanged behavior). With it, override ONLY the `a`
  // renderer to client-nav internal bare-slug hrefs; detection is syntactic here,
  // resolution is the host's (MarkdownView stays markdown-only).
  const components = useMemo<Components>(() => {
    if (!onInternalLink) return COMPONENTS;
    return {
      ...COMPONENTS,
      a: ({ href, children }) => {
        if (href && !/^(https?:|mailto:|#|\/)/.test(href)) {
          return (
            <a
              href={href}
              onClick={(e) => {
                e.preventDefault();
                onInternalLink(href);
              }}
              className="text-accent underline-offset-2 hover:underline cursor-pointer"
            >
              {children}
            </a>
          );
        }
        // #anchor links keep native in-page fragment behavior (no new tab) —
        // same branch as the static COMPONENTS renderer above.
        if (href && href.startsWith('#')) {
          return (
            <a href={href} className="text-accent underline-offset-2 hover:underline">
              {children}
            </a>
          );
        }
        return (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-accent underline-offset-2 hover:underline"
          >
            {children}
          </a>
        );
      },
    };
  }, [onInternalLink]);

  return (
    <div className={clsx('font-sans text-sm leading-relaxed text-ink-primary', className)}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeSlug]}
        components={components}
      >
        {markdown}
      </ReactMarkdown>
    </div>
  );
}
